import { useMemo } from "react";

import { css } from "@compiled/react";

import { Adg4DraggableTab } from "@/components/adg4/tabs/src/draggable-tab";
import { Adg4DraggableTabList } from "@/components/adg4/tabs/src/draggable-tab-list";
import { Adg4Tabs } from "@/components/adg4/tabs/src/tabs";
import { Tile, TileProps } from "@/components/tile/src/tile";
import { useLocale } from "@/libraries/web-framework/src/localization/use-locale";
import { withFeatureExposed } from "@/utils/feature-exposed/use-feature-exposed";
import { useLocation } from "@/utils/location-provider";

import {
    DEFAULT_CATEGORY_SLUG,
    useCategorySlug
} from "../../hooks/use-category-slug";

const displayPanel = css({
    width: "100%",
    overflowX: "auto",
    position: "sticky",
    insetBlockStart: "56px",
    zIndex: 1,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#fff",
    padding: "16px 0"
});

const categoriesSearchContainer = css({
    display: "flex",
    width: "100%",
    gap: 16
});

const categoriesContainer = css({
    flex: "1 1 0px",
    minWidth: "0%", // this is needed to constrain the children within the element box
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    padding: "2px"
});

export type TemplateCategoriesProps = {
    categories: {
        name: string;
        urlSlug: string;
        icon: TileProps["icon"];
        iconHovered: TileProps["iconHovered"];
    }[];
    onCategoryChange: (urlSlug: string) => void;
    onCategoryChangeIntent?: (urlSlug: string) => void;
};

const localizedLinkForCategory = (locale: string, categoryId: string) => {
    const prefix = locale === "en" ? "" : `/${locale}`;
    if (categoryId === "recommended") {
        return `${prefix}/software/confluence/templates`;
    }
    return `${prefix}/software/confluence/templates/categories/${categoryId}`;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP = () => {};

const TemplateCategoriesComponent = ({
    categories,
    onCategoryChange,
    onCategoryChangeIntent
}: TemplateCategoriesProps) => {
    const locale = useLocale();
    const location = useLocation();

    let currentSlug = useCategorySlug();

    // Use default slug if no category is selected and no search term is present.
    currentSlug = useMemo(() => {
        if (!currentSlug) {
            if (new URLSearchParams(location.search).get("term") === null) {
                return DEFAULT_CATEGORY_SLUG;
            }
        }
        return currentSlug;
    }, [currentSlug, location.search]);

    return (
        <div css={displayPanel}>
            <div
                css={categoriesSearchContainer}
                data-testid="confluence-template-categories"
            >
                <div css={css({ margin: "0 auto" })}>
                    <Adg4Tabs>
                        <Adg4DraggableTabList>
                            <div
                                css={categoriesContainer}
                                data-testid={"category-list"}
                            >
                                {categories.map((category) => (
                                    <Adg4DraggableTab key={category.urlSlug}>
                                        <Tile
                                            href={localizedLinkForCategory(
                                                locale,
                                                category.urlSlug
                                            )}
                                            label={category.name}
                                            onFocus={
                                                category.urlSlug !== currentSlug
                                                    ? () =>
                                                          onCategoryChangeIntent?.(
                                                              category.urlSlug
                                                          )
                                                    : undefined
                                            }
                                            onMouseEnter={
                                                category.urlSlug !== currentSlug
                                                    ? () =>
                                                          onCategoryChangeIntent?.(
                                                              category.urlSlug
                                                          )
                                                    : undefined
                                            }
                                            onClick={
                                                category.urlSlug !== currentSlug
                                                    ? () =>
                                                          onCategoryChange(
                                                              category.urlSlug
                                                          )
                                                    : NOOP
                                            }
                                            isActive={
                                                category.urlSlug === currentSlug
                                            }
                                            icon={category.icon}
                                            iconHovered={category.iconHovered}
                                        />
                                    </Adg4DraggableTab>
                                ))}
                            </div>
                        </Adg4DraggableTabList>
                    </Adg4Tabs>
                </div>
            </div>
        </div>
    );
};

export const TemplateCategories = withFeatureExposed(
    "cfind-1795-refresh-template-categories",
    "enrolled",
    TemplateCategoriesComponent
);
